
/**
 * Auth error types that can be returned by Supabase
 */
export interface AuthError {
  error: string;
  error_code?: string;
  error_description?: string;
}

/**
 * Parse error parameters from full URL
 */
export function parseAuthErrorFromUrl(url: string): AuthError | null {
  const hashIndex = url.indexOf('#')
  if (hashIndex === -1) {
    return null
  }

  const fragment = url.substring(hashIndex)
  return parseAuthErrorFromHash(fragment)
}

/**
 * Parse error parameters from URL hash fragment
 */
export function parseAuthErrorFromHash(hash: string): AuthError | null {
  if (!hash || !hash.includes('error=')) {
    return null;
  }

  // Remove the # if present and decode HTML entities
  let paramString = hash.replace('#', '');

  // Decode HTML entities like & to &
  paramString = paramString
    .replace(/&/g, '&')
    .replace(/</g, '<')
    .replace(/>/g, '>')
    .replace(/"/g, '"')
    .replace(/'/g, "'");

  console.log('[AuthErrorUtils] Original hash:', hash);
  console.log('[AuthErrorUtils] Decoded param string:', paramString);

  const params = new URLSearchParams(paramString);

  const error = params.get('error');
  if (!error) {
    console.log('[AuthErrorUtils] No error parameter found after parsing');
    return null;
  }

  const result = {
    error,
    error_code: params.get('error_code') || undefined,
    error_description: params.get('error_description') || undefined,
  };

  console.log('[AuthErrorUtils] Parsed error:', result);
  return result;
}

/**
 * Get user-friendly error message based on error code and type
 */
export function getErrorMessage(authError: AuthError): {
  title: string;
  description: string;
  isInviteError: boolean;
} {
  const { error, error_code, error_description } = authError;

  // Handle invite-specific errors
  if (error === 'access_denied' && error_code === 'otp_expired') {
    return {
      title: 'Invite Link Expired',
      description: 'This invitation link has expired. Please request a new invitation from your administrator.',
      isInviteError: true,
    };
  }

  if (error === 'access_denied' && error_description?.includes('invite')) {
    return {
      title: 'Invalid Invite Link',
      description: 'This invitation link is invalid or has already been used. Please request a new invitation from your administrator.',
      isInviteError: true,
    };
  }

  // Handle password reset errors
  if (error === 'access_denied' && error_code === 'otp_expired' && error_description?.includes('Email link')) {
    return {
      title: 'Reset Link Expired',
      description: 'This password reset link has expired. Please request a new password reset.',
      isInviteError: false,
    };
  }

  // Handle generic access denied
  if (error === 'access_denied') {
    return {
      title: 'Access Denied',
      description: error_description || 'Access to this resource has been denied. Please contact your administrator for assistance.',
      isInviteError: false,
    };
  }

  // Handle invalid token errors
  if (error === 'invalid_token') {
    return {
      title: 'Invalid Link',
      description: 'This authentication link is invalid. Please request a new link.',
      isInviteError: false,
    };
  }

  // Generic error fallback
  return {
    title: 'Authentication Error',
    description: error_description || `An authentication error occurred: ${error}. Please try again or contact support.`,
    isInviteError: false,
  };
}

/**
 * Clean error parameters from URL
 */
export function cleanupErrorUrl(): void {
  const url = new URL(window.location.href);

  // Remove error parameters from hash
  if (url.hash.includes('error=')) {
    // If hash only contains error params, clear it entirely
    if (url.hash.startsWith('#error=') || url.hash.startsWith('#&error=')) {
      url.hash = '';
    } else {
      // Remove error parameters but preserve other hash content
      const hashParts = url.hash.split(/[&#]/);
      const cleanHashParts = hashParts.filter(part =>
        !part.startsWith('error=') &&
        !part.startsWith('error_code=') &&
        !part.startsWith('error_description=')
      );
      url.hash = cleanHashParts.length > 1 ? cleanHashParts.join('#') : '';
    }
  }

  // Update URL without page reload
  if (window.location.href !== url.href) {
    window.history.replaceState(null, document.title, url.toString());
  }
}
